import React, { useState } from 'react';
import Text from '../../Text/Text';
import LabeledValue from '../../LabeledValue';
import StripePayment from '../../StripePayment/StripePayment';

interface PaymentStepProps {
  paymentData: {
    amount: string;
    platformFee: string;
    totalAmount: string;
    publicUserId: string;
    sellerId: string;
    itemId: string;
    promotionCode?: string;
    originalPlatformFee?: string;
    appliedPromotion?: any;
  };
  onBack: () => void;
  onPaymentSuccess: (transactionId: string) => void;
}

const PaymentStep: React.FC<PaymentStepProps> = ({ 
  paymentData, 
  onBack, 
  onPaymentSuccess 
}) => {
  const [isPaymentReady, setIsPaymentReady] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState('');

  // Payment handlers
  const handlePaymentReady = (isReady: boolean) => {
    setIsPaymentReady(isReady);
  };
  
  const handlePaymentProcessing = (isProcessing: boolean) => {
    setIsProcessingPayment(isProcessing);
  };
  
  const handlePaymentError = (error: string) => {
    setPaymentError(error);
    setIsProcessingPayment(false);
  };

  return (
    <div className="payment-step">
      <h2>Complete Your Payment</h2>
      
      {/* Payment Summary */}
      <div className="payment-summary">
        <h3>Payment Summary</h3>
        <div className="column-align-data-container">
          <LabeledValue
            label="Amount"
            value={`$${paymentData.amount}`}
            className="text-body"
            columnAligned={true}
          />
          {paymentData.promotionCode && paymentData.originalPlatformFee && (
            <>
              <LabeledValue
                label="Platform Fee (Original)"
                value={`$${paymentData.originalPlatformFee}`}
                className="text-body text-strikethrough"
                columnAligned={true}
              />
              <LabeledValue
                label={`Platform Fee (${paymentData.promotionCode})`}
                value={`$${paymentData.platformFee}`}
                className="text-body text-success"
                columnAligned={true}
              />
              <LabeledValue
                label="Promotion Savings"
                value={`-$${(parseFloat(paymentData.originalPlatformFee) - parseFloat(paymentData.platformFee)).toFixed(2)}`}
                className="text-body text-success"
                columnAligned={true}
              />
            </>
          )}
          {!paymentData.promotionCode && (
            <LabeledValue
              label="Platform Fee"
              value={`$${paymentData.platformFee}`}
              className="text-body"
              columnAligned={true}
            />
          )}
          <LabeledValue
            label="Total Amount"
            value={`$${paymentData.totalAmount}`}
            className="text-body text-bold"
            columnAligned={true}
          />
        </div>
        <LabeledValue
          label="Seller ID"
          value={`${paymentData.publicUserId}`}
          className="text-body" columnAligned={true}
        />
        <LabeledValue
          label="Item"
          value={paymentData.itemId}
          className="text-body"
        />

        {/* Action Buttons */}
        <div className="payment-actions">
          <button 
            className="payment-back-button"
            onClick={onBack}
            disabled={isProcessingPayment}
          >
            <Text className="text-body">
              Edit Details
            </Text>
          </button>
        </div>
      </div>
      
      {/* Payment Form */}
      <div className="payment-form-container">
        <Text className="text-body">Payment Details</Text>
        <StripePayment
          amount={paymentData.totalAmount}
          originalAmount={paymentData.amount}
          platformFee={paymentData.platformFee}
          publicUserId={paymentData.publicUserId}
          sellerId={paymentData.sellerId}
          itemId={paymentData.itemId}
          promotionCode={paymentData.promotionCode}
          originalPlatformFee={paymentData.originalPlatformFee}
          onReady={handlePaymentReady}
          onPaymentProcessing={handlePaymentProcessing}
          onPaymentError={handlePaymentError}
          onPaymentSuccess={onPaymentSuccess}
        />
      </div>
      
      {paymentError && (
        <Text className="text-caption text-error">
          {paymentError}
        </Text>
      )}
    </div>
  );
};

export default PaymentStep;